<template>
  <div class="consumption-gauges" v-if="hasData">
    <div class="cg-title">Consommation du mois</div>

    <div class="cg-row" v-for="g in gauges" :key="g.key">
      <div class="cg-head">
        <span class="cg-label">{{ g.label }}</span>
        <span class="cg-used" :class="g.unlimited ? 'green' : g.color">
          <template v-if="g.unlimited">illimité&nbsp;∞</template>
          <template v-else>used {{ g.percent }}%</template>
        </span>
      </div>
      <div class="cg-track">
        <div class="cg-fill" :class="g.unlimited ? 'green' : g.color" :style="{ width: (g.unlimited ? 100 : g.percent) + '%' }"></div>
      </div>
      <div v-if="!g.unlimited && g.color === 'red'" class="cg-cta">
        Crédit presque épuisé —
        <a href="#" @click.prevent="$emit('add-credit', g.key)">rajouter du crédit</a>
      </div>
    </div>

    <!-- Voix wolof : notes vocales reçues et réponses audio envoyées ce mois -->
    <div class="cg-row" v-if="audio">
      <div class="cg-head">
        <span class="cg-label">Audio wolof</span>
        <span class="cg-used" :class="audioCouleur">
          {{ audio.notes_recues }} reçues · {{ audio.reponses_vocales }} envoyées
        </span>
      </div>
      <div class="cg-track">
        <div class="cg-fill" :class="audioCouleur" :style="{ width: Math.max(2, creditAudio.pourcentage) + '%' }"></div>
      </div>
      <div v-if="!serviceActif" class="cg-cta">Service audio coupé — les agents répondent en texte.</div>
      <div v-else-if="creditAudio.epuise" class="cg-cta">Crédit audio épuisé — réponses en texte seulement.</div>
      <div v-else-if="creditAudio.alerte" class="cg-cta">Crédit audio presque épuisé.</div>
      <div v-if="pre" class="cg-cta">
        <template v-if="preEnCours">Préparation des audios… {{ pre.en_cache }} / {{ pre.textes }} phrases prêtes</template>
        <template v-else-if="pre.manquants > 0">
          {{ pre.en_cache }} / {{ pre.textes }} phrases prêtes —
          <a href="#" @click.prevent="prechauffer">préparer les audios des blocs</a>
        </template>
        <template v-else>Toutes les phrases des blocs sont déjà enregistrées.</template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { jarvisApiBaseUrl } from '@/config';
import { axiosPeelo } from '@/utils/axios';

export default {
  name: 'ConsumptionGauges',
  emits: ['add-credit'],
  props: {
    idbot: { type: String, required: true },
    // Intervalle de rafraîchissement auto (ms). 0 = désactivé.
    pollInterval: { type: Number, default: 15000 }
  },
  data() {
    return {
      loading: false,
      error: null,
      moo: null,
      griot: null,
      audio: null,
      pre: null,
      preEnCours: false,
      creditAudio: { pourcentage: 0, alerte: false, epuise: false },
      serviceActif: true,
      month: null,
      pollTimer: null
    };
  },
  computed: {
    hasData() {
      return !!(this.moo || this.griot || this.audio);
    },
    audioCouleur() {
      if (!this.serviceActif || this.creditAudio.epuise) return 'red';
      return this.creditAudio.alerte ? 'orange' : 'green';
    },
    gauges() {
      const out = [];
      if (this.moo) out.push({ key: 'moo', label: 'Moo', ...this.moo });
      if (this.griot) out.push({ key: 'griot', label: 'Griot', ...this.griot });
      return out;
    }
  },
  watch: {
    idbot: 'fetchUsage'
  },
  mounted() {
    this.fetchUsage();
    this.startPolling();
    // Pause quand l'onglet est masqué, refresh immédiat au retour
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.refresh);
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.refresh);
  },
  methods: {
    async fetchUsage() {
      if (!this.idbot || this.loading) return;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(`${jarvisApiBaseUrl}/api/usage/agent/${this.idbot}`);
        if (data && data.success) {
          this.moo = data.moo || null;
          this.griot = data.griot || null;
          this.month = data.month || null;
        }
      } catch (e) {
        this.error = (e && e.message) || 'Erreur de chargement';
      }
      // Voix wolof : silencieux si l'agent n'utilise pas l'audio ou si le
      // service n'est pas joignable — la jauge disparaît, rien ne casse.
      try {
        const { data } = await axiosPeelo.get(`/voix/usage/${this.idbot}`);
        if (data && data.success && (data.agent.appels > 0 || data.agent.notes_recues > 0)) {
          this.audio = data.agent;
          this.creditAudio = data.credit;
          this.serviceActif = data.service_actif;
        } else {
          this.audio = null;
        }
      } catch (e) { this.audio = null; }
      // État des audios pré-enregistrés de cet agent (silencieux si indisponible)
      try {
        const { data } = await axiosPeelo.get(`/voix/pregenerer/${this.idbot}`);
        if (data && data.success) {
          this.pre = { textes: data.textes, en_cache: data.en_cache, manquants: data.manquants };
          this.preEnCours = !!(data.chantier && !data.chantier.fini);
        }
      } catch (e) { this.pre = null; }
      this.loading = false;
    },
    startPolling() {
      this.stopPolling();
      if (this.pollInterval > 0) {
        this.pollTimer = setInterval(() => {
          if (!document.hidden) this.fetchUsage();
        }, this.pollInterval);
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    onVisibility() {
      if (!document.hidden) this.fetchUsage();
    },
    async prechauffer() {
      if (this.preEnCours) return;
      this.preEnCours = true;
      try { await axiosPeelo.post(`/voix/pregenerer/${this.idbot}`, {}); }
      catch (e) { this.preEnCours = false; }
      setTimeout(this.fetchUsage, 3000);
    },
    // Permet au parent de rafraîchir après un appel IA : this.$refs.gauges.refresh()
    refresh() {
      this.fetchUsage();
    }
  }
};
</script>

<style scoped>
.consumption-gauges {
  padding: 8px 15px 10px;
  margin-bottom: 8px;
}
.cg-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #e5e7eb;
  margin-bottom: 8px;
}
.cg-row {
  margin-bottom: 10px;
}
.cg-row:last-child {
  margin-bottom: 0;
}
.cg-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.cg-label {
  font-size: 13px;
  font-weight: 500;
  color: #e5e7eb;
}
.cg-used {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
}
.cg-track {
  position: relative;
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.05); /* même fond transparent que les blocs/pills de la sidebar */
  border-radius: 999px;
  overflow: hidden;
}
.cg-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease, background 0.3s ease;
}

/* Couleurs feu tricolore — alignées sur la palette du dashboard
   (vert menthe #DAFFD1 = état "ok"/actif Griot, rouge #ef4444 = alerte) */
.cg-fill.green  { background: #DAFFD1; box-shadow: 0 0 8px rgba(218, 255, 209, 0.4); }
.cg-fill.yellow { background: #f59e0b; }
.cg-fill.orange { background: #f97316; }
.cg-fill.red    { background: #ef4444; }

.cg-used.green  { color: #DAFFD1; }
.cg-used.yellow { color: #f59e0b; }
.cg-used.orange { color: #f97316; }
.cg-used.red    { color: #ef4444; }

.cg-cta {
  margin-top: 6px;
  font-size: 11px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1); /* même pill que les avertissements du dashboard */
  padding: 6px 8px;
  border-radius: 6px;
}
.cg-cta a {
  color: #ef4444;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}
</style>
